import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  Calendar, Clock, Video, X, RefreshCw, AlertCircle 
} from 'lucide-react';

interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  timezone: string;
  status: string;
  meetingUrl?: string;
  attendeeName: string;
  page: {
    title: string;
  };
}

export default function CustomerPortalDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/portal');
      return;
    }
    loadBookings();
  }, [token]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomerBookings(token!) as { bookings: Booking[] };
      setBookings(response.bookings);
    } catch (err: any) {
      setError(err.message);
      if (err.message.includes('Invalid') || err.message.includes('expired')) {
        navigate('/portal');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId: string) => {
    if (!confirm('Are you sure you want to cancel this meeting?')) return;
    
    try {
      setCancellingId(bookingId);
      await api.cancelCustomerBooking(token!, bookingId);
      
      // Refresh bookings
      await loadBookings();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCancellingId(null);
    }
  };

  const handleReschedule = async (bookingId: string) => {
    try {
      const result = await api.requestReschedule(token!, bookingId) as { rescheduleUrl: string };
      
      // Redirect to reschedule page
      window.location.href = result.rescheduleUrl;
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatDateTime = (startTime: string, endTime: string, timezone: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    return {
      date: start.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
      timezone,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">My Bookings</h1>
            <Button variant="ghost" onClick={() => navigate('/portal')}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {bookings.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-medium mb-2">No bookings yet</h2>
              <p className="text-gray-500">
                You don't have any upcoming meetings scheduled.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const formatted = formatDateTime(booking.startTime, booking.endTime, booking.timezone);
              const isPast = new Date(booking.endTime) < new Date();
              const canCancel = !isPast && booking.status !== 'CANCELLED';
              
              return (
                <Card key={booking.id} className={isPast ? 'opacity-60' : ''}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold">{booking.page.title}</h3>
                          <Badge 
                            variant={booking.status === 'CANCELLED' ? 'default' : isPast ? 'default' : 'success'}
                          >
                            {booking.status === 'CANCELLED' ? 'Cancelled' : isPast ? 'Completed' : 'Upcoming'}
                          </Badge>
                        </div>
                        
                        <div className="space-y-1 text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatted.date}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2" />
                            {formatted.time} ({formatted.timezone})
                          </div>
                        </div>

                        {booking.meetingUrl && !isPast && booking.status !== 'CANCELLED' && (
                          <a
                            href={booking.meetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-3 text-blue-600 hover:text-blue-700"
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Join Meeting
                          </a>
                        )}
                      </div>

                      {canCancel && (
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReschedule(booking.id)}
                            leftIcon={<RefreshCw className="w-4 h-4" />}
                          >
                            Reschedule
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancel(booking.id)}
                            isLoading={cancellingId === booking.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            leftIcon={<X className="w-4 h-4" />}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
